// components/sections/GapAnalysisItems.js - Version épurée sans confiance
import {
    BookOpen,
    ChevronRight,
    DollarSign,
    Edit3,
    Home,
    MoreVertical,
    Star,
    Target,
    Trash2,
    TrendingUp,
    Users
} from 'lucide-react';
import { useState } from 'react';

const GapAnalysisItems = ({ data, viewMode, onEdit, onDelete, onCreateActionPlan }) => {
    const [activeMenu, setActiveMenu] = useState(null);

    // Fonction pour obtenir l'icône selon la catégorie
    const getCategoryIcon = (category) => {
        const iconMap = {
            'Patrimoine Immobilier': Home,
            'patrimoine-immobilier': Home,
            'Cash Flow Immobilier': DollarSign,
            'revenus-locatifs': DollarSign,
            'Revenus Locatifs': DollarSign,
            'Compétences & Stratégies': BookOpen,
            'strategies-immobilieres': BookOpen,
            'Réseau & Visibilité': Users,
            'reseau-visibilite': Users,
            'Investissements Divers': TrendingUp,
            'Epargne urgence': Target,
            'Diversification Portfolio': Star,
            'default': Target
        };

        return iconMap[category] || iconMap['default'];
    };

    // Fonction pour obtenir le label de priorité épuré
    const getPriorityLabel = (priority) => {
        const priorityMap = {
            'low': 'Basse',
            'medium': 'Moyenne',
            'high': 'Haute'
        };
        return priorityMap[priority] || priority;
    };

    const renderCard = (item, index) => {
        const progressPercentage = (item.current / item.target) * 100;
        const gap = item.target - item.current;
        // Mise à jour des catégories monétaires
        const isMonetary = ['Patrimoine Immobilier', 'Cash Flow Immobilier', 'Revenus Locatifs', 'Investissements Divers', 'Epargne urgence', 'Diversification Portfolio'].includes(item.category);
        const unit = isMonetary ? '€' : '';

        const showMenu = activeMenu === item.id_objectif;
        const CategoryIcon = getCategoryIcon(item.category);
        const priorityLabel = getPriorityLabel(item.priority);

        return (
            <div key={item.id_objectif} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors shadow-sm hover:shadow-md">
                {/* En-tête gris inspiré de ProgressionStepsSection */}
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 flex items-center justify-center">
                                <CategoryIcon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="w-px h-9 bg-gray-300"></div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-700">{item.title || item.category}</h3>
                                <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">{item.category}</span>
                                    <span className="text-xs text-gray-300">–</span>
                                    <span className="text-xs font-medium text-gray-600">{item.timeline}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            {/* Priorité en étiquette grise épurée - tout à droite */}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {priorityLabel}
                            </span>

                            {/* Menu d'actions */}
                            {(onEdit || onDelete) && (
                                <div className="relative">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveMenu(showMenu ? null : item.id_objectif);
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                                    >
                                        <MoreVertical className="w-3.5 h-3.5" />
                                    </button>

                                    {showMenu && (
                                        <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[110px]">
                                            {onEdit && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onEdit(item);
                                                        setActiveMenu(null);
                                                    }}
                                                    className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                                >
                                                    <Edit3 className="w-3 h-3" />
                                                    <span>Modifier</span>
                                                </button>
                                            )}
                                            {onDelete && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(item);
                                                        setActiveMenu(null);
                                                    }}
                                                    className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                    <span>Supprimer</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contenu principal de la carte */}
                <div className="p-4">
                    {/* Progress */}
                    <div className="mb-3">
                        <div className="flex items-center justify-between mb-1.5 text-xs font-medium text-gray-700">
                            <span>Actuel</span>
                            <span className="font-bold text-gray-900">{Math.round(progressPercentage)}%</span>
                            <span>Objectif</span>
                        </div>
                        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${progressPercentage}%`, backgroundColor: '#6cb4ac' }}
                            />
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                            <div className="text-left">
                                <div className="text-sm font-bold text-gray-900">
                                    {item.current.toLocaleString('fr-FR')}{unit}
                                </div>
                                <div className="text-xs text-gray-500">Actuel</div>
                            </div>
                            <div className="text-center">
                                <div className={`text-xs font-semibold ${item.iconColor}`}>
                                    {gap.toLocaleString('fr-FR')}{unit}
                                </div>
                                <div className="text-xs text-gray-500">Écart</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-bold text-gray-900">
                                    {item.target.toLocaleString('fr-FR')}{unit}
                                </div>
                                <div className="text-xs text-gray-500">Objectif</div>
                            </div>
                        </div>
                    </div>

                    {/* Action */}
                    <button
                        onClick={() => onCreateActionPlan && onCreateActionPlan(item)}
                        className="w-full py-2 px-3  bg-[#7fa8a5] text-white rounded-lg font-medium hover:shadow-md transition-shadow duration-200 flex items-center justify-center group"
                    >
                        <span className="text-xs">Éditer le plan d'action</span>
                        <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        );
    };

    const renderTableRow = (item, index) => {
        const progressPercentage = (item.current / item.target) * 100;
        // Mise à jour des catégories monétaires
        const isMonetary = ['Patrimoine Immobilier', 'Cash Flow Immobilier', 'Revenus Locatifs', 'Investissements Divers', 'Epargne urgence', 'Diversification Portfolio'].includes(item.category);
        const unit = isMonetary ? '€' : '';

        const CategoryIcon = getCategoryIcon(item.category);
        const priorityLabel = getPriorityLabel(item.priority);

        return (
            <tr key={item.id_objectif} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                        {/* Icône de catégorie */}
                        <div className={`w-8 h-8 ${item.iconBgColor} rounded-lg flex items-center justify-center`}>
                            <CategoryIcon className={`w-4 h-4 ${item.iconColor}`} />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                                <div className="font-medium text-gray-900">{item.title || item.category}</div>
                                {/* Priorité en étiquette grise épurée */}
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                    {priorityLabel}
                                </span>
                            </div>
                            <div className="text-sm text-gray-500">
                                {item.category} • {item.current.toLocaleString('fr-FR')}{unit} / {item.target.toLocaleString('fr-FR')}{unit}
                            </div>
                        </div>
                    </div>
                </td>
                <td className="py-4 px-4">
                    <div className="w-full">
                        <div className="text-center mb-1">
                            <span className="text-xs font-bold text-gray-900">{Math.round(progressPercentage)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="h-2 rounded-full"
                                style={{
                                    width: `${progressPercentage}%`,
                                    backgroundColor: '#6cb4ac'
                                }}
                            />
                        </div>
                    </div>
                </td>
                <td className="py-4 px-4">
                    <span className="text-sm">{item.timeline}</span>
                </td>
                <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => onCreateActionPlan && onCreateActionPlan(item)}
                            className="text-sm px-3 py-1 text-white rounded-md hover:shadow-md transition-shadow"
                            style={{ backgroundColor: '#7fa8a5' }}
                        >
                            Plan d'action
                        </button>

                        {/* Actions d'édition */}
                        {(onEdit || onDelete) && (
                            <div className="flex items-center space-x-1">
                                {onEdit && (
                                    <button
                                        onClick={() => onEdit(item)}
                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
                                        title="Modifier"
                                    >
                                        <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        onClick={() => onDelete(item)}
                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded"
                                        title="Supprimer"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    // Fermer le menu si on clique ailleurs
    const handleClickOutside = (e) => {
        // Vérifier si le clic est à l'extérieur d'un menu
        if (activeMenu && !e.target.closest('.relative')) {
            setActiveMenu(null);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto" onClick={handleClickOutside}>
            {viewMode === 'card' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                    {data.map(renderCard)}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Objectif</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Progression</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Délai</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(renderTableRow)}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default GapAnalysisItems;